/**
 * The Organization → Product → Environment state machine.
 *
 * Pure on purpose. A context selector looks like navigation furniture, but the
 * value it holds is carried into every scoped request that follows it — so a
 * wrong product id here is not a display bug, it is the shape of a data-isolation
 * bug. Keeping the transitions as a pure function means the invariants can be
 * proven directly, without a renderer, a router or a query cache in the way.
 *
 * The invariant, in one line: an environment implies a product, and a product
 * implies an organization.
 *
 *   (null, null, null)          nothing selected
 *   (O,    null, null)          organization only
 *   (O,    P,    null)          product, no environment
 *   (O,    P,    E)             fully specified
 *
 * Every other shape is unreachable rather than merely unrendered.
 *
 * WHY TRANSITIONS CARRY THEIR PARENT
 *
 * `selectProduct` takes the organization the product belongs to, and
 * `selectEnvironment` takes its product. That looks redundant — the caller
 * usually has the right parent selected already — but it is what makes
 * "product from another organization" impossible to express rather than merely
 * unlikely. The alternative is validating against whatever the query cache
 * happens to hold, which is a race: during a parent switch the cache is empty,
 * and a check against it would reject legitimate selections and accept illegal
 * ones a moment later.
 *
 * `reconcile` is the second half. Transitions enforce structure; reconcile
 * enforces server truth — a product that was archived, an environment that
 * disappeared, an organization whose membership was revoked. Both are needed:
 * structure cannot know what the server did, and server truth arrives too late
 * to prevent an impossible intermediate state.
 */

export interface CatalogContextState {
  readonly organizationId: string | null;
  readonly productId: string | null;
  readonly environmentId: string | null;
}

export const EMPTY_CONTEXT: CatalogContextState = {
  organizationId: null,
  productId: null,
  environmentId: null,
};

export type CatalogTransition =
  | { readonly type: 'selectOrganization'; readonly organizationId: string }
  | {
      readonly type: 'selectProduct';
      readonly productId: string;
      /** The organization this product belongs to, as the caller understands it. */
      readonly organizationId: string;
    }
  | {
      readonly type: 'selectEnvironment';
      readonly environmentId: string;
      /** The product this environment belongs to, as the caller understands it. */
      readonly productId: string;
    }
  | { readonly type: 'clearProduct' }
  | { readonly type: 'clearEnvironment' }
  | {
      /**
       * Server truth. Ids absent from these lists are no longer selectable —
       * archived, deleted, or in an organization this person has left.
       *
       * `products` and `environments` are `null` while unknown (not yet loaded),
       * which is deliberately different from `[]` (loaded, and there are none).
       * Treating "not yet known" as "gone" would clear a valid selection every
       * time a parent query refetched.
       */
      readonly type: 'reconcile';
      readonly organizationIds: readonly string[];
      readonly productIds: readonly string[] | null;
      readonly environmentIds: readonly string[] | null;
    };

export function catalogContextReducer(
  state: CatalogContextState,
  transition: CatalogTransition,
): CatalogContextState {
  switch (transition.type) {
    case 'selectOrganization': {
      if (transition.organizationId === state.organizationId) return state;
      // Atomic, in one transition. Clearing descendants in a follow-up effect
      // leaves a window where the bar reads "new organization / old product",
      // and — worse — where the old product id is still live enough to be sent
      // as a child query parameter scoped to the new organization.
      return {
        organizationId: transition.organizationId,
        productId: null,
        environmentId: null,
      };
    }

    case 'selectProduct': {
      // A product from an organization that is not the selected one is not a
      // state to render carefully; it is a state that must not exist.
      if (transition.organizationId !== state.organizationId) return state;
      if (transition.productId === state.productId) return state;
      return { ...state, productId: transition.productId, environmentId: null };
    }

    case 'selectEnvironment': {
      if (state.productId === null) return state;
      if (transition.productId !== state.productId) return state;
      return { ...state, environmentId: transition.environmentId };
    }

    case 'clearProduct':
      if (state.productId === null && state.environmentId === null) return state;
      return { ...state, productId: null, environmentId: null };

    case 'clearEnvironment':
      if (state.environmentId === null) return state;
      return { ...state, environmentId: null };

    case 'reconcile': {
      let next = state;

      if (
        next.organizationId !== null &&
        !transition.organizationIds.includes(next.organizationId)
      ) {
        // Membership is gone. Everything below it was scoped by it.
        next = EMPTY_CONTEXT;
      }

      if (
        next.productId !== null &&
        transition.productIds !== null &&
        !transition.productIds.includes(next.productId)
      ) {
        next = { ...next, productId: null, environmentId: null };
      }

      if (
        next.environmentId !== null &&
        (next.productId === null ||
          (transition.environmentIds !== null &&
            !transition.environmentIds.includes(next.environmentId)))
      ) {
        next = { ...next, environmentId: null };
      }

      return isSameContext(next, state) ? state : next;
    }
  }
}

export function isSameContext(a: CatalogContextState, b: CatalogContextState): boolean {
  return (
    a.organizationId === b.organizationId &&
    a.productId === b.productId &&
    a.environmentId === b.environmentId
  );
}

/**
 * Whether a state is one the model permits.
 *
 * Exported so tests can assert it over every reachable state rather than over a
 * list of cases somebody remembered to write down.
 */
export function isValidContext(state: CatalogContextState): boolean {
  if (state.productId !== null && state.organizationId === null) return false;
  if (state.environmentId !== null && state.productId === null) return false;
  return true;
}
